import React from 'react';

import * as styles from './CountdownRing.styles';

export interface CountdownRingProps {
  /** Bump to restart the countdown from empty — e.g. the active slide index. */
  cycleKey: number;
  interval: number;
  /** Called when the countdown completes, to advance the carousel. */
  onComplete: () => void;
  paused: boolean;
}

/**
 * The ring overlaid on the "next" arrow that fills to visualise the time remaining until autoplay
 * advances — and which doubles as the autoplay timer itself.
 *
 * The fill is a CSS keyframe animation lasting `interval` ms. Because it *is* the timer, pausing
 * comes for free: `animation-play-state: paused` freezes the ring exactly where it is and resumes
 * from the same point, so hovering a slide or arrow holds the countdown rather than restarting it.
 * When the animation finishes we advance via `onComplete`; the resulting slide change bumps
 * `cycleKey`, remounting the circle so the next countdown starts from empty.
 */
export const CountdownRing = ({ cycleKey, interval, onComplete, paused }: CountdownRingProps) => (
  <svg
    aria-hidden="true"
    css={styles.ring}
    data-testid="autoplay-countdown"
    height={styles.SIZE}
    viewBox={`0 0 ${styles.SIZE} ${styles.SIZE}`}
    width={styles.SIZE}
  >
    <circle css={styles.track} cx={styles.CENTRE} cy={styles.CENTRE} r={styles.RADIUS} />
    <circle
      css={styles.progress}
      cx={styles.CENTRE}
      cy={styles.CENTRE}
      data-countdown-progress
      key={cycleKey}
      onAnimationEnd={onComplete}
      r={styles.RADIUS}
      style={{
        animationDuration: `${interval}ms`,
        animationPlayState: paused ? 'paused' : 'running',
      }}
    />
  </svg>
);
